import { Box, Grid, SxProps } from '@mui/material';
import {
    TextField,
    SelectField,
    TextArea,
    Switch,
    UrlField,
    type Option,
    type CategorizedOption,
    Checkbox
} from '../input';
import { ComboBox, type ComboBoxProps } from '../input/combo-box';
import { Info } from '../info';
import React from 'react';

type FieldType =
    | 'text'
    | 'select'
    | 'textarea'
    | 'switch'
    | 'url'
    | 'checkbox'
    | 'placeholder'
    | 'combobox';

export type DependsOn = { name: string; value: string[] };

export type FieldColumns = 1 | 2 | 3;

export type FieldConfig = {
    label?: React.ReactNode;
    placeholder?: string;
    maxLength?: number;
    required?: boolean;
    disabled?: boolean;
    infoText?: string;
    helpTopicPath?: string;
    name: string;
    options?: Option[];
    categorizedOptions?: CategorizedOption[];
    emptyOption?: Option;
    type: FieldType;
    readOnly?: boolean;
    error?: string | null | undefined;
    helperText?: string | null | undefined;
    sx?: SxProps;
    value?: any;
    renderOption?: ComboBoxProps['renderOption'];
    filterOptions?: ComboBoxProps['filterOptions'];
    iconSize?: number;
    dependsOn?: DependsOn[];
    columns?: FieldColumns;
    [key: string]: any;
};

export type FieldEventHandlers = {
    onChange?: (name: string, value: any) => void;
    onBlur?: (name: string) => void;
    onFocus?: (name: string) => void;
};

const Placeholder = () => {
    return <Box />;
};

const FieldComponent: Record<FieldType, React.ComponentType<any>> = {
    text: TextField,
    select: SelectField,
    textarea: TextArea,
    switch: Switch,
    url: UrlField,
    checkbox: Checkbox,
    placeholder: Placeholder,
    combobox: ComboBox
};

const createFieldLabel = (config: FieldConfig): React.ReactNode => {
    const { label: configLabel, infoText, helpTopicPath, required, iconSize } = config;

    if (!helpTopicPath) {
        return configLabel;
    }

    return (
        <Info
            infoText={infoText}
            helpTopicPath={helpTopicPath}
            sx={{ display: 'inline-flex' }}
            iconSize={iconSize ?? 11}
        >
            <Box component="span" sx={{ marginRight: 0.5 }}>
                {configLabel}
            </Box>
            {required && (
                <Box component="span" sx={{ color: 'error.main', marginRight: 0.5 }}>
                    *
                </Box>
            )}
        </Info>
    );
};

const createBaseEventHandlers = (name: string, eventHandlers?: FieldEventHandlers) => {
    if (!eventHandlers) return {};

    return {
        onBlur: eventHandlers.onBlur ? () => eventHandlers.onBlur!(name) : undefined,
        onFocus: eventHandlers.onFocus ? () => eventHandlers.onFocus!(name) : undefined
    };
};

const createSelectEventHandlers = (name: string, eventHandlers?: FieldEventHandlers) => {
    const baseHandlers = createBaseEventHandlers(name, eventHandlers);

    return {
        ...baseHandlers,
        onChange: eventHandlers?.onChange
            ? (event: any, child: React.ReactNode) => {
                  const value = event.target?.value ?? event;
                  eventHandlers.onChange!(name, value);
              }
            : undefined
    };
};

const createComboboxEventHandlers = (name: string, eventHandlers?: FieldEventHandlers) => {
    const baseHandlers = createBaseEventHandlers(name, eventHandlers);

    return {
        ...baseHandlers,
        onChange: eventHandlers?.onChange
            ? (event: { target: { value: string } }) => {
                  eventHandlers.onChange!(name, event.target.value);
              }
            : undefined
    };
};

const createStandardEventHandlers = (name: string, eventHandlers?: FieldEventHandlers) => {
    const baseHandlers = createBaseEventHandlers(name, eventHandlers);

    return {
        ...baseHandlers,
        onChange: eventHandlers?.onChange
            ? (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                  const target = e.target;
                  let value: any;

                  if (target.type === 'checkbox') {
                      value = (target as HTMLInputElement).checked;
                  } else if (target.type === 'number') {
                      value = Number(target.value);
                  } else {
                      value = target.value;
                  }

                  eventHandlers.onChange!(name, value);
              }
            : undefined
    };
};

const createEventHandlers = (type: FieldType, name: string, eventHandlers?: FieldEventHandlers) => {
    switch (type) {
        case 'select':
            return createSelectEventHandlers(name, eventHandlers);
        case 'combobox':
            return createComboboxEventHandlers(name, eventHandlers);
        default:
            return createStandardEventHandlers(name, eventHandlers);
    }
};

/**
 * Only these wire `helperText` / `error` into MUI inputs that consume them.
 * Switch, checkbox, etc. spread `...props` onto elements that may forward to the DOM,
 * where `error={false}` and `helperText` trigger React warnings.
 */
const FIELD_TYPES_WITH_MUI_FIELD_PROPS: FieldType[] = [
    'text',
    'select',
    'textarea',
    'url',
    'combobox'
];

export const FormField = ({
    config,
    eventHandlers
}: {
    config: FieldConfig;
    eventHandlers?: FieldEventHandlers;
}) => {
    const {
        type,
        error,
        helperText,
        options,
        categorizedOptions,
        emptyOption,
        name,
        required,
        infoText,
        ...rest
    } = config;

    const Component = FieldComponent[type];
    const label = createFieldLabel(config);
    const eventHandlersForField = createEventHandlers(type, name, eventHandlers);

    const props = {
        label,
        ...(FIELD_TYPES_WITH_MUI_FIELD_PROPS.includes(type)
            ? {
                  /** Omit when false so nothing forwards `error={false}` to a DOM node */
                  error: error ? true : undefined,
                  helperText
              }
            : {}),
        required: infoText ? false : required,
        ...eventHandlersForField,
        ...rest
    };

    const selectOnlyProps =
        type === 'select'
            ? {
                  categorizedOptions: categorizedOptions as CategorizedOption[],
                  emptyOption: emptyOption as Option
              }
            : undefined;

    const optionListProps =
        type === 'select' || type === 'combobox' ? { options: options as Option[] } : undefined;

    return (
        <Component
            {...props}
            {...optionListProps}
            {...selectOnlyProps}
        />
    );
};

export const FormFieldGrid = ({
    children,
    columns = 3,
    sx
}: {
    children: React.ReactNode;
    columns?: FieldColumns;
    sx?: SxProps;
}) => (
    <Grid container spacing={[1, 2]} sx={sx}>
        {React.Children.map(children, child => (
            <Grid size={{ xs: 12, md: columns === 3 ? 4 : columns === 2 ? 6 : 12 }}>{child}</Grid>
        ))}
    </Grid>
);
